import { EventEmitter } from 'events';

import { StagingArea, StagingAreaOptions, StagingEvents } from './types.js';
import { parseItemsFn, stagingAreaActionHashFn } from './utils.js';

/**
 * Staging area for actions to be committed with events
 */
export class StagingAreaImpl<TItem, TMeta>
  extends EventEmitter<StagingEvents<TItem, TMeta>>
  implements StagingArea<TItem, TMeta>
{
  stack: TItem[];
  hashActionFn: (item: TItem) => string = stagingAreaActionHashFn;
  parseItemsFn: (items: TItem[], hashFn: (item: TItem) => string) => TItem[] = parseItemsFn;

  constructor(stack: TItem[] = [], opts?: StagingAreaOptions<TItem>) {
    super();
    const { hashActionFn = this.hashActionFn, parseItemsFn = this.parseItemsFn } = { ...opts };

    this.hashActionFn = hashActionFn;
    this.parseItemsFn = parseItemsFn;

    this.stack = stack;
  }

  prepare(abort: boolean = false) {
    if (abort) {
      this.abort();
    }

    if (this.stack.length !== 0) {
      throw new Error('Staging stack is not empty, please commit or abort first');
    }
    return this;
  }

  abort(reason?: string) {
    // nothing staged, nothing to abort — emitting here would trigger a full
    // store rebuild in every subscriber for a state that did not change
    if (this.stack.length === 0) {
      return this;
    }

    this.stack = [];
    this.emit('abort', reason);
    this.emit('change');
    return this;
  }

  add(item: TItem | TItem[]) {
    const items = Array.isArray(item) ? item : [item];
    this.emit('add', items);
    this.stack = this.parseItemsFn([...this.stack, ...items], this.hashActionFn);
    this.emit('change');
    return this;
  }

  comparator(a: TItem, b: TItem) {
    return this.hashActionFn(a) === this.hashActionFn(b);
  }

  commit(meta: TMeta) {
    if (this.stack.length === 0) {
      throw new Error('Staging stack is empty, please add actions first');
    }
    this.emit('precommit');
    this.emit('commit', this.stack, meta);
    this.stack = [];
    this.emit('change');
    return this;
  }

  subscribe(cb: () => void) {
    const listener = () => cb();
    this.on('change', listener);
    return () => {
      this.off('change', listener);
    };
  }

  toArray() {
    return Array.from(this);
  }

  toJSON(): TItem[] {
    return this.toArray();
  }

  fromJSON(json: any): StagingArea<TItem, TMeta> {
    if (!Array.isArray(json)) {
      throw new Error('Invalid staging JSON');
    }

    return new StagingAreaImpl(json);
  }

  *[Symbol.iterator]() {
    for (let i = 0; i < this.stack.length; i++) {
      yield this.stack[i];
    }
  }
}

export const createStagingArea = <TItem = unknown, TMeta = unknown>(
  stack: TItem[] = [],
  opts?: StagingAreaOptions<TItem>
) => {
  return new StagingAreaImpl<TItem, TMeta>(stack, opts);
};
